export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig();
  const {currentUserId, currentUserSseTopics} = useWadAuth();
  const notificationsStore = useNotificationsStore();
  const { isOnline } = useConnectivity();

  let es: EventSource | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;
  // Only a reconnect needs reconciliation. The first successful open happens during page load,
  // where loadEvent() has just fetched counts and bookmarks anyway.
  let hasConnectedBefore = false;

  // Backoff bounds for a dead stream. Jitter matters more than usual here: if Mercure drops
  // during the registration burst, every connected attendee reconnects at once, and a fixed
  // delay would turn one outage into a synchronised stampede against the hub and then — via
  // the reconciliation fetch below — against /workshops/counts.
  const RETRY_BASE_MS = 1000;
  const RETRY_MAX_MS = 30000;

  const buildUrl = (): URL | null => {
    try {
      const url = new URL(runtimeConfig.public.mercureUrl as string);
      url.searchParams.append('topic', runtimeConfig.public.wadSseGlobalTopic as string);
      for (const topic of currentUserSseTopics.value ?? []) {
        url.searchParams.append('topic', topic);
      }
      return url;
    } catch {
      return null;
    }
  };

  const clearRetry = () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  // Cancel a pending backoff AND forget the attempts that preceded it, so the next failure
  // starts from RETRY_BASE_MS again. Only for events that make the earlier failures
  // irrelevant: a successful open, an explicit disconnect, or the network coming back.
  const resetRetry = () => {
    clearRetry();
    retryAttempt = 0;
  };

  const scheduleRetry = () => {
    if (retryTimer !== null) return;

    const backoff = Math.min(RETRY_BASE_MS * 2 ** retryAttempt, RETRY_MAX_MS);
    // Full jitter: anywhere in [0, backoff) rather than clustering on the boundary.
    const delay = Math.random() * backoff;
    retryAttempt++;

    retryTimer = setTimeout(() => {
      retryTimer = null;
      connect();
    }, delay);
  };

  const connect = () => {
    // A CLOSED EventSource never reopens itself, and it used to leave `es` non-null forever —
    // which made this early return permanent and killed live updates for the rest of the
    // session with no way back. Only a live (OPEN or CONNECTING) stream should block a retry.
    if (es && es.readyState !== EventSource.CLOSED) return;
    // The app's connectivity state, not navigator.onLine: a stuck flag used to skip the SSE
    // connection for the whole session (see useConnectivity).
    if (!isOnline.value) return;
    if (!currentUserId.value) return;

    const url = buildUrl();
    if (!url) return;

    // Only a CLOSED stream can still be here — the guard above returned for live ones — so this
    // is a no-op in practice; it just guarantees the old handle is released before the new one
    // takes its place.
    es?.close();

    es = new EventSource(url.toString(), { withCredentials: false });

    es.onopen = () => {
      resetRetry();

      if (hasConnectedBefore) {
        // Messages published while the stream was down are gone — Mercure is not replayed here
        // — so the local counts are stale by an unknown amount. Only a re-read of the server
        // can recover; see the handler in app.vue.
        window.dispatchEvent(new CustomEvent('wad:sse-reconnected'));
      }
      hasConnectedBefore = true;
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        notificationsStore.handleNewSseEvent(data as AppNotification);
      } catch {
        // Ignore malformed SSE payloads
      }
    };

    es.onerror = () => {
      // EventSource retries transient failures itself and sits in CONNECTING while it does;
      // interfering there would fight the browser. CLOSED is the terminal state it never
      // recovers from on its own, and that is the case this plugin has to handle.
      if (es?.readyState === EventSource.CLOSED) {
        es = null;
        scheduleRetry();
      }
    };
  };

  const disconnect = () => {
    resetRetry();
    if (es) {
      es.close();
      es = null;
    }
  };

  const reconnect = () => {
    disconnect();
    connect();
  };

  // React to authentication and topic changes: connect on login/session
  // hydration, disconnect on logout, and rebuild the subscription whenever the
  // user's SSE topics change. reconnect() always closes first, so duplicate
  // EventSource connections can't accumulate.
  watch(
    () => `${currentUserId.value ?? ''}|${JSON.stringify(currentUserSseTopics.value)}`,
    () => {
      if (!currentUserId.value) {
        disconnect();
      } else {
        reconnect();
      }
    },
    { immediate: true },
  );

  // Coming back online cancels any pending backoff: waiting out a 30s timer after the network
  // is demonstrably back is the difference between "instant" and "app looks broken". Not
  // reconnect(), which is the disconnect-then-connect above and would drop a live stream.
  const connectNow = () => {
    resetRetry();
    connect();
  };

  // The app's connectivity state, not the browser's events — the same source connect() gates on.
  //
  // navigator's events are the wrong trigger in both directions. 'offline' is only a suspicion
  // that useConnectivity confirms with a probe, so acting on it directly killed the stream for a
  // false alarm that the rest of the app shrugged off. And nothing guarantees an 'online' ever
  // arrives: plugins/api.ts marks us online on every successful response, and the recheck probe
  // clears a stuck flag on its own, so the state routinely goes false -> true with no event at
  // all. A connect() that bailed on the isOnline guard schedules no retry, so without this watch
  // the stream stayed dead for the rest of the session — the precise failure useConnectivity
  // exists to stop the app from getting stuck in.
  watch(isOnline, (online) => {
    if (!online) {
      disconnect();
      return;
    }

    connectNow();
  });

  // Kept as an extra trigger, not as the state: a stream that died on its own leaves isOnline
  // untouched (nothing failed a request), so the watch above stays silent and only the backoff
  // would recover it. The event is still the earliest proof the network is back, and connect()
  // is a no-op on a live stream.
  window.addEventListener('online', connectNow);

  // Returning to a backgrounded PWA is the other common way to arrive with a dead stream —
  // mobile browsers freeze or drop connections in the background without firing an error.
  // connect() decides whether there is anything to do; a live stream makes it a no-op. The
  // attempt counter is deliberately kept, so a resume can't be used to shortcut the backoff.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    clearRetry();
    connect();
  });
});
